import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './QueriesManagement.css';

function QueriesManagement() {
  const { apiFetch } = useAuth();
  const { language } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState('');

  const CATEGORY_ORDER = ['playerPhone', 'itemsWeapons', 'vehiclesHousing', 'economyBilling', 'donate', 'jobsCompanies', 'other'];

  const getCategory = (key) => {
    const upper = String(key || '').toUpperCase();
    if (upper.startsWith('PLAYER_') || upper.startsWith('PHONES_')) return 'playerPhone';
    if (upper.startsWith('ITEMS_') || upper.startsWith('WEAPON_')) return 'itemsWeapons';
    if (upper.startsWith('VEHICLES_') || upper.startsWith('HOUSING_')) return 'vehiclesHousing';
    if (upper.startsWith('ECONOMY_') || upper.startsWith('BILLING_')) return 'economyBilling';
    if (upper.startsWith('DONATE_')) return 'donate';
    if (upper.startsWith('JOBS_') || upper.startsWith('COMPANIES_')) return 'jobsCompanies';
    return 'other';
  };

  const text = language === 'da'
    ? {
        title: 'Queries',
        subtitle: 'Site-specifikke SQL skabeloner. Standard SQL bruges automatisk, hvis en query nulstilles.',
        loading: 'Indlæser queries...',
        save: 'Gem',
        saving: 'Gemmer...',
        reset: 'Nulstil til standard',
        updated: 'Query opdateret.',
        resetDone: 'Query nulstillet til standard.',
        key: 'Nøgle',
        description: 'Forklaring',
        sql: 'SQL',
        mode: 'Tilstand',
        modeDefault: 'Standard',
        modeOverride: 'Override',
        search: 'Søg i queries...',
        category: 'Kategori',
        noResults: 'Ingen queries matcher din søgning.',
        warningTitle: '⚠️ Teknisk opsætning - Databasekonfiguration',
        warningText: 'Dette er en avanceret teknisk sektion til konfigurering af, hvordan panelet interagerer med din database. Hver query bestemmer, hvordan data hentes, opdateres og slettes. Forkert SQL kan forhindre systemet i at fungere eller forårsage datatab. Alle ændringer er på dine egne risiko - du bærer fuldt ansvar for databasekonfigurationen. Kontroller venligst grundigt før du gemmer, og test aldrig på en produktionsserver uden backup.',
        categories: {
          playerPhone: 'Spiller & telefon',
          itemsWeapons: 'Items & våben',
          vehiclesHousing: 'Køretøjer & boliger',
          economyBilling: 'Økonomi & faktura',
          donate: 'Donation',
          jobsCompanies: 'Jobs & virksomheder',
          other: 'Andet'
        }
      }
    : {
        title: 'Queries',
        subtitle: 'Site-specific SQL templates. Defaults are used automatically when a query is reset.',
        loading: 'Loading queries...',
        save: 'Save',
        saving: 'Saving...',
        reset: 'Reset to default',
        updated: 'Query updated.',
        resetDone: 'Query reset to default.',
        key: 'Key',
        description: 'Description',
        sql: 'SQL',
        mode: 'Mode',
        modeDefault: 'Default',
        modeOverride: 'Override',
        search: 'Search queries...',
        category: 'Category',
        noResults: 'No queries match your search.',
        warningTitle: '⚠️ Technical Setup - Database Configuration',
        warningText: 'This is an advanced technical section for configuring how the admin panel interacts with your database. Each query determines how data is retrieved, updated, and deleted. Incorrect SQL can break the system or cause data loss. All changes are at your own risk — you bear full responsibility for database configuration. Please verify carefully before saving, and never test on a production server without backups.',
        categories: {
          playerPhone: 'Player & Phone',
          itemsWeapons: 'Items & Weapons',
          vehiclesHousing: 'Vehicles & Housing',
          economyBilling: 'Economy & Billing',
          donate: 'Donate',
          jobsCompanies: 'Jobs & Companies',
          other: 'Other'
        }
      };

  const localizeDescription = (description) => {
    if (language !== 'da') return description;
    let textDesc = String(description || '');
    const replacements = [
      ['Searches ', 'Søger '],
      ['Searches', 'Søger'],
      ['Loads ', 'Indlæser '],
      ['Loads', 'Indlæser'],
      ['Returns ', 'Returnerer '],
      ['Updates ', 'Opdaterer '],
      ['Deletes ', 'Sletter '],
      ['Creates ', 'Opretter '],
      ['Saves ', 'Gemmer '],
      ['Scans ', 'Scanner '],
      ['Finds ', 'Finder '],
      ['Builds ', 'Bygger '],
      ['Checks ', 'Tjekker '],
      ['records', 'poster'],
      ['record', 'post'],
      ['player', 'spiller'],
      ['players', 'spillere'],
      ['phone', 'telefon'],
      ['phones', 'telefoner'],
      ['vehicle', 'køretøj'],
      ['vehicles', 'køretøjer'],
      ['housing', 'bolig'],
      ['billing', 'faktura'],
      ['economy', 'økonomi'],
      ['filters', 'filtre'],
      ['query', 'query'],
      ['queries', 'queries']
    ];
    replacements.forEach(([from, to]) => {
      textDesc = textDesc.replaceAll(from, to);
    });
    return textDesc;
  };

  const filteredTemplates = templates.filter((template) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return (
      String(template.key || '').toLowerCase().includes(needle) ||
      String(template.description || '').toLowerCase().includes(needle) ||
      String(template.sql || '').toLowerCase().includes(needle)
    );
  });

  const groupedTemplates = CATEGORY_ORDER.map((categoryName) => ({
    name: categoryName,
    templates: filteredTemplates.filter((template) => getCategory(template.key) === categoryName)
  })).filter((group) => group.templates.length > 0);

  const [collapsed, setCollapsed] = useState(() => {
    const initial = {};
    CATEGORY_ORDER.forEach((c) => { initial[c] = false; });
    return initial;
  });

  const refs = React.useRef({});
  const setGroupRef = (key, el) => { refs.current[key] = el; };

  const toggleCategory = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleJump = (categoryKey) => {
    if (!categoryKey) return;
    // expand chosen category and collapse others for clarity
    const newState = {};
    CATEGORY_ORDER.forEach((c) => { newState[c] = c !== categoryKey; });
    setCollapsed(newState);
    // scroll into view
    setTimeout(() => {
      const el = refs.current[categoryKey];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const loadTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/site-query-templates');
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to load templates');

      const rows = payload.templates || [];
      setTemplates(rows);
      const initialDrafts = {};
      rows.forEach((row) => {
        initialDrafts[row.key] = row.sql;
      });
      setDrafts(initialDrafts);
    } catch (err) {
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const saveTemplate = async (key) => {
    setSavingKey(key);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(`/api/site-query-templates/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: drafts[key] || '' })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to update template');
      setMessage(text.updated);
      await loadTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey('');
    }
  };

  const resetTemplate = async (key) => {
    setSavingKey(key);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch(`/api/site-query-templates/${encodeURIComponent(key)}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to reset template');
      setMessage(text.resetDone);
      await loadTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey('');
    }
  };

  return (
    <div className="queries-management">
      <div className="queries-header">
        <h2>{text.title}</h2>
        <p>{text.subtitle}</p>
      </div>

      <div className="queries-warning-banner">
        <div className="queries-warning-icon">⚠️</div>
        <div className="queries-warning-content">
          <h3>{text.warningTitle}</h3>
          <p>{text.warningText}</p>
        </div>
      </div>

      <div className="queries-toolbar">
        <input
          className="queries-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={text.search}
        />
        <div className="queries-jump">
          <select onChange={(e) => handleJump(e.target.value)} defaultValue="">
            <option value="">{text.jumpPlaceholder || (language === 'da' ? 'Gå til kategori...' : 'Jump to category...')}</option>
            {CATEGORY_ORDER.map((cat) => (
              <option key={cat} value={cat}>{text.categories[cat] || cat}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="queries-error">{error}</div>}
      {message && <div className="queries-success">{message}</div>}

      {loading ? (
        <div className="queries-loading">{text.loading}</div>
      ) : groupedTemplates.length === 0 ? (
        <div className="queries-empty">{text.noResults}</div>
      ) : (
        <div className="queries-group-list">
          {groupedTemplates.map((group) => (
            <div key={group.name} className="queries-group" ref={(el) => setGroupRef(group.name, el)}>
              <div className="queries-group-head" onClick={() => toggleCategory(group.name)} role="button" tabIndex={0}>
                <div className="queries-label">{text.category}</div>
                <h3>
                  {text.categories[group.name] || group.name}
                  <span className="queries-group-count">{group.templates.length}</span>
                </h3>
                <div className={`group-caret ${collapsed[group.name] ? 'rotated' : ''}`}>▾</div>
              </div>

              <div className={`queries-list ${collapsed[group.name] ? 'collapsed' : ''}`}>
                {group.templates.map((template) => (
                  <div key={template.key} className="queries-card">
                    <div className="queries-card-top">
                      <div>
                        <div className="queries-label">{text.key}</div>
                        <strong>{template.key}</strong>
                      </div>
                      <div>
                        <div className="queries-label">{text.mode}</div>
                        <span className={`queries-mode ${template.isDefault ? 'default' : 'override'}`}>
                          {template.isDefault ? text.modeDefault : text.modeOverride}
                        </span>
                      </div>
                    </div>

                    <div className="queries-label">{text.description}</div>
                    <div className="queries-description">{localizeDescription(template.description || '-')}</div>

                    <div className="queries-label">{text.sql}</div>
                    <textarea
                      className="queries-sql"
                      value={drafts[template.key] || ''}
                      onChange={(e) => updateDraft(template.key, e.target.value)}
                      spellCheck={false}
                    />

                    <div className="queries-actions">
                      <button
                        className="queries-btn primary"
                        onClick={() => saveTemplate(template.key)}
                        disabled={savingKey === template.key}
                      >
                        {savingKey === template.key ? text.saving : text.save}
                      </button>
                      <button
                        className="queries-btn"
                        onClick={() => resetTemplate(template.key)}
                        disabled={savingKey === template.key}
                      >
                        {text.reset}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default QueriesManagement;
